import {announce} from '@github-ui/aria-live'
import {goBack} from '@github-ui/history'
import type {UserSettingsOptionConfig} from '@github-ui/issue-create/getSafeConfig'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {useRepoIssuesSidebarLayoutEnabled} from '@github-ui/repo-issues-page-layout/useSidebarLayoutEnabled'
import type {CreateMilestoneInput} from '@github-ui/server-persisted-queries/types/createRepositoryMilestoneMutation'
import type {MilestoneState} from '@github-ui/server-persisted-queries/types/updateMilestoneMutation'
import {useNavigate} from '@github-ui/use-navigate'
import {type Dispatch, type SetStateAction, useCallback, useState} from 'react'

import {MilestoneForm, type MilestoneInitialValues} from './components/MilestoneForm'
import {LABELS} from './constants/labels'
import type {EditRepositoryResult} from './milestone-edit-query-types'
import styles from './MilestoneCreateEdit.module.css'
import {MilestoneError} from './MilestoneError'
import {useUpdateMilestoneDetails} from './use-update-milestone-details'
import {useUpdateMilestoneState} from './use-update-milestone-state'
import {formatFormErrorMessage} from './utils/format-form-error-message'

export type EditMilestoneInput = {
  id: string
  title: string
  description?: string | null
  dueOn?: string | null
}

export type MilestoneEditFragment = Pick<
  EditRepositoryResult,
  'id' | 'nameWithOwner' | 'viewerCanUpdateMilestones' | 'milestone'
> & {viewerCanPush?: boolean}

type MilestoneEditProps = {
  repository: MilestoneEditFragment
  optionConfig?: UserSettingsOptionConfig
  onMutationSuccess?: (milestoneNumber: number) => void | Promise<void>
  onOptimisticSubmit?: (input: EditMilestoneInput, state?: MilestoneState) => void
  draft?: MilestoneInitialValues
  submissionError?: string
  isPending?: boolean
}

export function MilestoneEdit({
  repository: repositoryData,
  optionConfig,
  onMutationSuccess,
  onOptimisticSubmit,
  draft,
  submissionError,
  isPending,
}: MilestoneEditProps) {
  const [submissionErrors, setSubmissionErrors] = useState<string | null>(null)
  const navigate = useNavigate()

  // Set and announce a submission error together. The error renders as a Primer Banner
  // (a focusable section, not a live region) with no focus moved to it, so a screen-reader
  // user would otherwise not learn the edit failed. Routing both the error paths through
  // this covers WCAG 4.1.3 without reacting to state in an effect.
  const showSubmissionError = useCallback((message: string | null) => {
    setSubmissionErrors(message)
    if (message) {
      announce(message, {assertive: true})
    }
  }, [])

  const sidebarLayoutEnabled = useRepoIssuesSidebarLayoutEnabled()
  const {mutate: updateMilestoneState} = useUpdateMilestoneState()

  const updateMilestoneDetailsTanstack = useUpdateMilestoneDetails({
    setSubmissionErrors: showSubmissionError,
    onSuccess: async () => {
      if (repositoryData.milestone) {
        await onMutationSuccess?.(repositoryData.milestone.number)
        navigate(`/${repositoryData.nameWithOwner}/milestone/${repositoryData.milestone.number}`)
      }
    },
  })

  const handleFormSubmit = useCallback(
    (input: CreateMilestoneInput, setIsSubmitting: Dispatch<SetStateAction<boolean>>) => {
      setSubmissionErrors(null)

      if (!repositoryData.milestone) {
        showSubmissionError(LABELS.milestoneErrorMessage)
        setIsSubmitting(false)
        return
      }

      if (!repositoryData.viewerCanUpdateMilestones) {
        showSubmissionError(LABELS.milestoneEditPermissionError)
        setIsSubmitting(false)
        return
      }

      const editingInput: EditMilestoneInput = {
        id: repositoryData.milestone.id,
        title: input.title,
        description: input.description,
        dueOn: input.dueOn,
      }

      if (onOptimisticSubmit) {
        onOptimisticSubmit(editingInput)
        setIsSubmitting(false)
        return
      }

      updateMilestoneDetailsTanstack(
        {
          repositoryNameWithOwner: repositoryData.nameWithOwner,
          milestoneNumber: repositoryData.milestone.number,
          input: editingInput,
        },
        setIsSubmitting,
      )
    },
    [onOptimisticSubmit, repositoryData, showSubmissionError, updateMilestoneDetailsTanstack],
  )

  const handleToggleMilestoneState = useCallback(
    (e: React.MouseEvent, setIsSubmitting: Dispatch<SetStateAction<boolean>>, input: MilestoneInitialValues) => {
      e.preventDefault()
      setIsSubmitting(true)
      setSubmissionErrors(null)

      if (!repositoryData?.milestone) {
        showSubmissionError(LABELS.milestoneErrorMessage)
        setIsSubmitting(false)
        return
      }
      const milestoneNumber = repositoryData.milestone.number

      if (!repositoryData.viewerCanUpdateMilestones) {
        showSubmissionError(LABELS.milestoneEditPermissionError)
        setIsSubmitting(false)
        return
      }

      const isClosed = repositoryData.milestone.closed
      const nextState = isClosed ? 'OPEN' : 'CLOSED'

      if (onOptimisticSubmit) {
        onOptimisticSubmit(
          {
            id: repositoryData.milestone.id,
            title: input.title ?? '',
            description: input.description,
            dueOn: input.dueOn,
          },
          nextState,
        )
        setIsSubmitting(false)
        return
      }

      updateMilestoneState(
        {
          id: repositoryData.milestone.id,
          number: repositoryData.milestone.number,
          nameWithOwner: repositoryData.nameWithOwner,
          state: nextState,
          operation: {
            type: 'setFields',
            id: repositoryData.milestone.id,
            fields: {closed: nextState === 'CLOSED'},
          },
        },
        {
          onError: (error: Error) => {
            setIsSubmitting(false)
            if (error.cause && Array.isArray(error.cause)) {
              showSubmissionError(formatFormErrorMessage(error.cause))
            } else {
              showSubmissionError(LABELS.milestoneEditError)
            }
          },
          onSuccess: async () => {
            await onMutationSuccess?.(milestoneNumber)
            navigate(`/${repositoryData.nameWithOwner}/milestone/${milestoneNumber}`)
          },
        },
      )
    },
    [navigate, onMutationSuccess, onOptimisticSubmit, repositoryData, updateMilestoneState, showSubmissionError],
  )

  if (!repositoryData?.milestone) {
    return (
      <div>
        <MilestoneError title={LABELS.milestoneError} message={LABELS.milestoneErrorMessage} />
      </div>
    )
  }

  const initialValues: MilestoneInitialValues = {
    id: repositoryData.milestone.id,
    title: repositoryData.milestone.title,
    description: repositoryData.milestone.description,
    dueOn: repositoryData.milestone.dueOn,
  }

  const handleFormCancel = () => {
    goBack()
  }

  return (
    <div className={sidebarLayoutEnabled ? undefined : styles.middlePaneWrapper} data-testid="milestone-edit">
      <ErrorBoundary
        boundaryName="milestone-edit"
        fallback={<MilestoneError title={LABELS.milestonePageError} message={LABELS.milestonePageErrorMessage} />}
      >
        <MilestoneForm
          repository={repositoryData}
          optionConfig={optionConfig}
          formTitle={LABELS.editMilestoneTitle}
          formSubmitLabel={LABELS.saveChanges}
          onSubmit={handleFormSubmit}
          onCancel={handleFormCancel}
          submissionErrors={submissionErrors ?? submissionError}
          initialValues={draft ?? initialValues}
          isPending={isPending}
          onToggleMilestoneState={handleToggleMilestoneState}
          toggleStateLabel={repositoryData.milestone.closed ? LABELS.reopenMilestone : LABELS.closeMilestone}
        />
      </ErrorBoundary>
    </div>
  )
}
